import React from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { getBestTextColor } from './helpers/accessibilityHelpers';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    height: '210px',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
    position: 'relative',
    '&:hover $openBtn': { opacity: 1 },
  },
  band: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    '& span': { opacity: 0, transition: 'opacity 0.12s ease' },
    '&:hover span': { opacity: 1 },
  },
  openBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(255,255,255,0.85)',
    color: '#222',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    '&:hover': { backgroundColor: '#fff' },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.5rem 0.25rem 0',
    color: theme.palette.text.secondary,
  },
  likes: { fontSize: '0.85rem', fontWeight: 600 },
}));

/**
 * A single explore palette card: a vertical stack of colors (click a band to
 * copy its hex), a like toggle, and an "open in generator" action.
 */
function ExploreCard({ palette, liked, onToggleLike, onCopy }) {
  const classes = useStyles();
  const history = useHistory();

  const openInGenerator = () => {
    const hex = palette.colors.map(c => c.replace('#', '')).join('-');
    history.push(`/generate/${hex}`);
  };

  return (
    <div className={classes.card}>
      <div className={classes.stack}>
        {palette.colors.map((color, i) => (
          <div
            key={`${color}-${i}`}
            className={classes.band}
            style={{ backgroundColor: color, color: getBestTextColor(color) }}
            onClick={() => onCopy(color)}
            role="button"
            aria-label={`Copy ${color.replace('#', '').toUpperCase()}`}
          >
            <span>{color.replace('#', '').toUpperCase()}</span>
          </div>
        ))}
        <Tooltip title="Open in generator" arrow>
          <IconButton
            size="small"
            className={classes.openBtn}
            onClick={openInGenerator}
            aria-label={`Open ${palette.id} in generator`}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      <div className={classes.footer}>
        <Tooltip title={liked ? 'Unlike' : 'Like'} arrow>
          <IconButton
            size="small"
            onClick={() => onToggleLike(palette.id)}
            aria-label={liked ? `Unlike ${palette.id}` : `Like ${palette.id}`}
            style={{ color: liked ? '#e53935' : 'inherit' }}
          >
            {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <span className={classes.likes}>{palette.likes + (liked ? 1 : 0)}</span>
      </div>
    </div>
  );
}

export default ExploreCard;
